import { jsPDF } from 'jspdf';
import type { CotizacionDto } from '@/types/cotizacion';

const PAGE_W = 210;
const PAGE_H = 297;
const ML = 14;  // margin left
const MR = 14;  // margin right
const CW = PAGE_W - ML - MR;
const BREAK_Y = PAGE_H - 20;

const DARK: [number, number, number]    = [30, 30, 30];
const BLUE: [number, number, number]    = [15, 35, 60];
const GRAY: [number, number, number]    = [100, 100, 100];
const LGRAY: [number, number, number]   = [220, 220, 225];
const WHITE: [number, number, number]   = [255, 255, 255];
const LBGBLUE: [number, number, number] = [235, 240, 250];

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtRange(desde: string, hasta: string) {
  return `${fmtDate(desde + 'T00:00:00Z')} – ${fmtDate(hasta + 'T00:00:00Z')}`;
}

function usd(v: number) {
  return `$${v.toFixed(2)}`;
}

function screenDims(cot: CotizacionDto): string {
  if (!cot.detalles || cot.detalles.length === 0) return '—';
  return cot.detalles.map((d) => {
    let anchoM: string, altoM: string;
    if (d.pantallaModular) {
      anchoM = ((d.columnas * d.pantallaModular.anchoModuloMm) / 1000).toFixed(2);
      altoM  = ((d.filas    * d.pantallaModular.altoModuloMm)  / 1000).toFixed(2);
    } else {
      anchoM = ((d.columnas * d.producto!.anchoCm) / 100).toFixed(2);
      altoM  = ((d.filas    * d.producto!.altoCm)  / 100).toFixed(2);
    }
    return `${anchoM}m × ${altoM}m`;
  }).join(', ');
}

export function generateReportePdf(
  cotizaciones: CotizacionDto[],
  desde: string,
  hasta: string,
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Agrupar por cotizador ────────────────────────────────────────────────
  const grupos = new Map<string, CotizacionDto[]>();
  for (const c of cotizaciones) {
    const key = c.usuarioNombre;
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key)!.push(c);
  }

  let y = 0;
  let firstPage = true;

  const addPage = () => {
    if (!firstPage) doc.addPage();
    firstPage = false;
    // Header de página
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, PAGE_W, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...WHITE);
    doc.text('REPORTE DE COTIZACIONES — EXCTECLED', ML, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Período: ${fmtRange(desde, hasta)}`, PAGE_W - MR, 12, { align: 'right' });
    y = 26;
  };

  const checkBreak = (needed: number) => {
    if (y + needed > BREAK_Y) addPage();
  };

  addPage();

  // ── Por cada grupo de cotizador ──────────────────────────────────────────
  let isFirstGroup = true;
  for (const [cotizador, cots] of grupos) {
    checkBreak(30);

    if (!isFirstGroup) y += 4;
    isFirstGroup = false;

    // Cabecera del cotizador
    doc.setFillColor(...LBGBLUE);
    doc.roundedRect(ML, y, CW, 10, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...BLUE);
    doc.text(`COTIZADOR: ${cotizador.toUpperCase()}`, ML + 4, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text(`${cots.length} cotización${cots.length !== 1 ? 'es' : ''}`, PAGE_W - MR, y + 7, { align: 'right' });
    y += 13;

    // Cabecera de columnas
    doc.setFillColor(...BLUE);
    doc.rect(ML, y, CW, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...WHITE);
    const C = {
      fecha:     { x: ML + 2,   w: 18  },
      proyecto:  { x: ML + 21,  w: 28  },
      cliente:   { x: ML + 50,  w: 30  },
      telefono:  { x: ML + 81,  w: 24  },
      correo:    { x: ML + 106, w: 44  },
      pantalla:  { x: ML + 151, w: 22  },
      total:     { x: PAGE_W - MR, w: 0 },
    };
    doc.text('Fecha',    C.fecha.x,    y + 5);
    doc.text('Proyecto', C.proyecto.x, y + 5);
    doc.text('Cliente',  C.cliente.x,  y + 5);
    doc.text('Teléfono', C.telefono.x, y + 5);
    doc.text('Correo',   C.correo.x,   y + 5);
    doc.text('Pantalla', C.pantalla.x, y + 5);
    doc.text('Total',    C.total.x,    y + 5, { align: 'right' });
    y += 7;

    // Filas de cotizaciones
    for (let i = 0; i < cots.length; i++) {
      checkBreak(10);
      const c = cots[i];
      const rowBg = i % 2 === 0 ? WHITE : [248, 249, 252] as [number, number, number];
      doc.setFillColor(...rowBg);
      doc.rect(ML, y, CW, 8, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...DARK);

      const clip = (s: string | null | undefined, max: number) =>
        !s ? '—' : s.length > max ? s.slice(0, max - 1) + '…' : s;

      doc.text(fmtDate(c.fechaCreacion),              C.fecha.x,    y + 5.5);
      doc.text(clip(c.nombreProyecto, 18),            C.proyecto.x, y + 5.5);
      doc.text(clip(c.cliente.nombre, 20),            C.cliente.x,  y + 5.5);
      doc.text(clip(c.cliente.telefono, 16),          C.telefono.x, y + 5.5);
      doc.text(clip(c.cliente.correo, 28),            C.correo.x,   y + 5.5);
      doc.text(screenDims(c),                         C.pantalla.x, y + 5.5);
      doc.text(usd(c.totalFinal),                     C.total.x,    y + 5.5, { align: 'right' });

      // Separador
      doc.setDrawColor(...LGRAY);
      doc.setLineWidth(0.1);
      doc.line(ML, y + 8, PAGE_W - MR, y + 8);
      y += 8;
    }

    // Total del cotizador
    y += 4;
  }

  // ── Número de página ─────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(`Pág. ${p} / ${totalPages}`, PAGE_W - MR, PAGE_H - 6, { align: 'right' });
    doc.text(`Generado: ${new Date().toLocaleDateString('es-PE')}`, ML, PAGE_H - 6);
  }

  doc.save(`reporte_cotizaciones_${desde}_${hasta}.pdf`);
}
